import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import React from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { AccruedLiabilitiesConfig } from './AccruedLiabilitiesConfig';
import { BorrowingBaseMetricsConfig } from './BorrowingBaseMetricsConfig';
import { FinancialCovenantResultsConfig } from './FinancialCovenantResultsConfig';
import { FundingRequestMetricsConfig } from './FundingRequestMetricsConfig';
import { OrgProvidedInputsConfig } from './OrgProvidedInputsConfig';
import { PortfolioCovenantResultsConfig } from './PortfolioCovenantResultsConfig';
import { TextInputFormField } from 'packages/common-frontend/components/FormField';

type DataAccessorEditOperatorProps = {
  formProps: FormikProps<CreditAgreement.Config.Reporting.Inputs.DataAccessor.Accessor>;
};
const DataAccessorEditOperator = ({
  formProps,
}: DataAccessorEditOperatorProps) => {
  const { values } = formProps;
  switch (values.method) {
    case 'accruedLiabilities':
      return <AccruedLiabilitiesConfig values={values} formProps={formProps} />;
    case 'borrowingBaseMetrics':
      return (
        <BorrowingBaseMetricsConfig values={values} formProps={formProps} />
      );
    case 'creditAgreementMetrics':
      // currently no config for credit agreement metrics to author
      return <></>;
    case 'backupServicingCycleMetrics':
      // no config to author: the servicing cycle is resolved from the worklease
      return <></>;
    case 'fundingRequestMetrics':
      return (
        <FundingRequestMetricsConfig values={values} formProps={formProps} />
      );
    case 'invoiceMetrics':
      // currently no config for invoice metrics to author
      return <></>;
    case 'loanMetrics':
      // currently no config for loan metrics to author
      return <></>;
    case 'portfolioCovenantResults':
      return (
        <PortfolioCovenantResultsConfig values={values} formProps={formProps} />
      );
    case 'orgProvidedInputs':
      return <OrgProvidedInputsConfig values={values} formProps={formProps} />;
    case 'financialCovenantResults':
      return (
        <FinancialCovenantResultsConfig values={values} formProps={formProps} />
      );
    default:
      return finleyAssertUnreachable(values);
  }
};

type EditDataAccessorModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (
    accessor: CreditAgreement.Config.Reporting.Inputs.DataAccessor.Accessor,
  ) => void;
  dataAccessor: CreditAgreement.Config.Reporting.Inputs.DataAccessor.Accessor;
};
export const EditDataAccessorModal = ({
  isOpen,
  onCancel,
  onSave,
  dataAccessor,
}: EditDataAccessorModalProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent p="5" maxWidth="min-content" minWidth="3xl">
        <ModalHeader>
          Edit Data Accessor: &quot;{dataAccessor.method}&quot;
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Formik
            initialValues={{ ...dataAccessor }}
            onSubmit={(props) => {
              onSave(props);
            }}
          >
            {(
              formProps: FormikProps<CreditAgreement.Config.Reporting.Inputs.DataAccessor.Accessor>,
            ) => (
              <Form onSubmit={formProps.handleSubmit}>
                <TextInputFormField label="Name" name="name" />

                <Box my="6">
                  <DataAccessorEditOperator formProps={formProps} />
                </Box>

                <Flex justifyContent="center">
                  <Button type="submit" mr="2">
                    Done
                  </Button>
                  <Button onClick={() => onCancel()}>Cancel</Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
